import React, { useMemo } from "react";
import { CardLegalStatus } from "@/components";
import { CardCountry } from "@/components";
import {
  CountryData,
  CountryCheckStatus,
} from "@/components";
import { CardSectionRate } from "@/components";
import { formatDate } from "@/helpers/helpers";
import { DatabaseCheck, mapStolenDatabasesToCountries, TheftDatabaseStatus } from "@/api/mappers/hsp/api";

interface LegalDatabaseCheck {
  name: string;
  variant: "success" | "danger" | "info";
  description: string;
  badgeText: string;
}

export interface HSPLegalProps {
  pledgeRegisters?: DatabaseCheck[];
  theftDatabases?: DatabaseCheck[];
  verificationDate?: string;
}

/**
 * Mapuje DatabaseCheck na LegalDatabaseCheck dla polskich baz danych
 */
const mapDatabaseCheckToLegalDatabaseCheck = (
  dbCheck: DatabaseCheck,
): LegalDatabaseCheck => {
  if (dbCheck.status === TheftDatabaseStatus.FOUND_IN_STOLEN_DB) {
    return {
      name: dbCheck.displayName,
      variant: "danger",
      description: "Pojazd widnieje w bazie danych",
      badgeText: "Problematyczny",
    };
  }

  if (dbCheck.status === TheftDatabaseStatus.PROBLEM_WITH_DB_CONNECTION) {
    return {
      name: dbCheck.displayName,
      variant: "info",
      description: "Nie odnaleziono informacji w rejestrze.",
      badgeText: "Brak danych",
    };
  }

  return {
    name: dbCheck.displayName,
    variant: "success",
    description: "Brak wpisów w bazie danych",
    badgeText: "Czysty",
  };
};

/**
 * Określa status ogólny na podstawie danych z countries i polishDatabases
 */
const determineOverallStatus = (
  countries: CountryData[],
  polishDatabases: LegalDatabaseCheck[],
): "warning" | "danger" | "success" => {
  // Sprawdź czy w countries występuje status FOUND
  const hasFoundInCountries = countries.some(
    (country) => country.status === CountryCheckStatus.FOUND
  );

  // Sprawdź czy w polishDatabases występuje variant "danger"
  const hasDangerInPolishDatabases = polishDatabases.some(
    (database) => database.variant === "danger"
  );

  // Jeśli w którymś arrayu występuje problem, ustaw status na danger
  if (hasFoundInCountries || hasDangerInPolishDatabases) {
    return "danger";
  }

  // Problem z połączeniem do bazy oznacza brak pewnego wyniku - ostrzeżenie zamiast "Prawidłowy"
  const hasUnknownInCountries = countries.some(
    (country) => country.status === CountryCheckStatus.UNKNOWN
  );
  const hasInfoInPolishDatabases = polishDatabases.some(
    (database) => database.variant === "info"
  );

  if (hasUnknownInCountries || hasInfoInPolishDatabases) {
    return "warning";
  }

  // Jeśli nie ma problemów, zwróć success
  return "success";
};

export const HSPLegal: React.FC<HSPLegalProps> = ({
  pledgeRegisters = [],
  theftDatabases = [],
  verificationDate,
}) => {
  // Dodatek "bazy pojazdów skradzionych" jest wykupiony tylko gdy API zwróciło wpisy
  const hasStolenDatabases = theftDatabases.length > 0;

  const hasFoundInCountries = useMemo(
    () =>
      theftDatabases.some(
        (country) => country.status === TheftDatabaseStatus.FOUND_IN_STOLEN_DB
      ),
    [theftDatabases]
  );

  const hasStolenDbConnectionProblem = useMemo(
    () =>
      theftDatabases.some(
        (country) =>
          country.status === TheftDatabaseStatus.PROBLEM_WITH_DB_CONNECTION
      ),
    [theftDatabases]
  );

  // Problem z połączeniem do którejkolwiek bazy (kradzionych lub rejestru zastawów)
  const hasConnectionProblem = useMemo(
    () =>
      [...theftDatabases, ...pledgeRegisters].some(
        (db) => db.status === TheftDatabaseStatus.PROBLEM_WITH_DB_CONNECTION
      ),
    [theftDatabases, pledgeRegisters]
  );

  const stolenDbCheck: LegalDatabaseCheck = hasFoundInCountries
    ? {
      name: "Bazy pojazdów skradzionych",
      variant: "danger",
      description: "Uwaga! Pojazd figuruje w bazach pojazdów skradzionych.",
      badgeText: "Problematyczny",
    }
    : hasStolenDbConnectionProblem
      ? {
        name: "Bazy pojazdów skradzionych",
        variant: "info",
        description: "Nie odnaleziono informacji w rejestrze.",
        badgeText: "Brak danych",
      }
      : {
        name: "Bazy pojazdów skradzionych",
        variant: "success",
        description: "Nie znaleziono wpisów w bazach pojazdów skradzionych.",
        badgeText: "Czysty",
      };
  // Mapuj dane z API na format wymagany przez komponenty
  const polishDatabases: LegalDatabaseCheck[] = pledgeRegisters.map(
    (dbCheck) => mapDatabaseCheckToLegalDatabaseCheck(dbCheck)
  );

  const countries: CountryData[] =
    mapStolenDatabasesToCountries(theftDatabases);

  // Określ status ogólny na podstawie danych
  const overallStatus = determineOverallStatus(countries, polishDatabases);

  // Określ rating i wiadomość na podstawie statusu
  const getRatingAndMessage = (status: "warning" | "danger" | "success") => {
    switch (status) {
      case "danger":
        return { rating: 1, message: "Problematyczny" };
      case "warning":
        return { rating: 3, message: "Ostrzeżenie" };
      case "success":
        return { rating: 5, message: "Prawidłowy" };
    }
  };

  const { rating, message } = getRatingAndMessage(overallStatus);

  return (
    <div className="mt-6 space-y-8 lg:mt-10">
      {/* Nagłówek sekcji */}

      {/* Blok oceny ogólnej — pomijany przy samym dodatku rejestru (bez baz
          kradzionych) oraz gdy brak połączenia z którąś z baz, gdzie status
          ogólny wprowadzałby w błąd; pokazujemy wtedy tylko wynik przeszukania */}
      {hasStolenDatabases && !hasConnectionProblem && (
        <div className="max-w-[400px]">
          <CardSectionRate
            rating={rating}
            title={"Status ogólny"}
            variant="light"
            ratingMessage={message}
            verificationDate={
              verificationDate ? formatDate(verificationDate) || "" : ""
            }
          />
        </div>
      )}

      {/* Sprawdzenia w polskich bazach */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {hasStolenDatabases && (
          <CardLegalStatus
            variant={stolenDbCheck.variant}
            title={stolenDbCheck.name}
            iconName="document-task"
            description={stolenDbCheck.description}
            badgeText={stolenDbCheck.badgeText}
          />
        )}
        {polishDatabases.map((database, index) => (
          <CardLegalStatus
            key={index}
            variant={database.variant}
            title={database.name}
            description={database.description}
            badgeText={database.badgeText}
          />
        ))}
      </div>

      {/* Sprawdzenia w bazach krajowych */}
      <div className="grid grid-cols-1 gap-4 justify-items-start items-start md:grid-cols-2 lg:gap-6 2xl:grid-cols-3">
        {countries.map((country, index) => (
          <CardCountry
            key={index}
            country={country}
            status={country.status}
            recommendation={country.recommendation}
            i18n={{
              recommendationLabel: "Rekomendacja:",
              flagAltTemplate: "Flaga {country}",
              statusNotRecorded: "Nie odnotowano",
              statusRecorded: "Odnotowano",
              statusNoData: "Brak danych",
              comment: "Ewidencja pojazdów skradzionych",
            }}
          />
        ))}
      </div>
    </div>
  );
};
